import { DeleteOutlined, EditOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { BasicResponseDTO } from '@dto/basicResponse.dto';
import {
  ConfigurationChangeFilter,
  ConfigurationChangeFilterDTO,
  ConfigurationChangeFiltersPreviewDTO,
  GetConfigurationChangeFilterRule,
} from '@dto/configurationChanges.dto';
import { Button, Popconfirm } from 'antd';
import axios, { AxiosError } from 'axios';
import { FC, lazy, useEffect, useRef, useState } from 'react';
import { XmButton } from 'src/component/button/XmButton';
import { RuleGroup } from 'src/component/selectItems/XmRuleList';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import XmTabsSticky from 'src/component/stickyTab/XmTabsSticky';
import { Log } from 'src/service/log.service';
import { getApi } from 'src/util/apiCalls';
import { GLOB } from 'src/util/Glob';
import { usePrompt } from '../../../../hook/usePrompt';
import './CfgFilters.less';
import FilterModal, { PropsBase as FormProps } from './modal/Modal';

const CfgTrackerPart = lazy(() => import('@parts/cfgTracker/CfgTrackerPart'));

export interface IFormFilter {
  class: string;
  hw_type: string;
  subsystem: string;
  rule: RuleGroup & { filter_rule_id?: string; };
  properties: string[];
}
export interface IFormLabel {
  label: string;
}

interface FilterData extends ConfigurationChangeFilter {
  editData: GetConfigurationChangeFilterRule[] | undefined;
  isNew: boolean;
}

const CfgFilter: FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<FilterData[]>([]);
  const [activeTab, setActiveTab] = useState<string>();
  const init = useRef(false);
  const controller = useRef(new AbortController());
  const [formProps, setFormProps] = useState<FormProps & { open: boolean; }>();

  usePrompt({ message: 'You have unsaved Filter!' }, filters.some(f => f.isNew));

  useEffect(() => {
    getApi<ConfigurationChangeFiltersPreviewDTO>(
      '/api/configuration/v1/tracker/filters?type=tracker',
      controller.current.signal
    )
      .then(
        ({ data: { data } }) =>
          setFilters(
            data.map((filter): FilterData => ({ ...filter, editData: undefined, isNew: false }))
          ),
        (reason) => Log.error('Failed to get filters list!', reason)
      )
      .finally(() => setIsLoading(false));

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      controller.current.abort();
    };
  }, []);

  if (!init.current && !isLoading) {
    init.current = true;
    setActiveTab(filters?.[0].filter_id);
  }

  const handleFilterChange =
    (filterId: string): FormProps['onFormFinish'] =>
      (data) => {
        setActiveTab(data.filter_id);
        setFilters((prev) =>
          prev.map((filter): FilterData => {
            if (filter.filter_id !== filterId) return filter;

            return { ...data, isNew: false, editData: data.rules };
          })
        );
      };

  const handleAddFilter = () => {
    setFilters((prev): FilterData[] => {
      const newFilterId = `new-${Date.now().toString()}`;
      const label = setNewFilterName(prev.map((filter) => filter.label));
      const editData: GetConfigurationChangeFilterRule[] = [];
      setActiveTab(newFilterId);
      setFormProps({
        // modal opening
        label,
        open: true,
        isCreate: true,
        filterId: newFilterId,
        data: undefined,
        groupId: undefined,
        usedFilterLabels: prev.map((filter) => filter.label),
        onFormFinish: handleFilterChange(newFilterId),
      });

      return [
        {
          filter_id: newFilterId,
          isNew: true,
          label,
          editData,
        },
        ...prev,
      ];
    });
  };

  const handleEditFilter =
    ({ filter_id, isNew, editData, label, group_id }: FilterData) =>
      () => {
        if (!isNew && !editData) {
          return new Promise<void>((resolve, reject) => {
            getApi<ConfigurationChangeFilterDTO>(
              `/api/configuration/v1/tracker/filter/${filter_id}`,
              controller.current.signal
            ).then(
              ({ data: { data } }) => {
                setFilters((prev) =>
                  prev.map((filter) => {
                    if (filter.filter_id !== filter_id) return filter;

                    setFormProps({
                      // modal opening
                      open: true,
                      filterId: filter_id,
                      label: filter.label,
                      data: data.rules,
                      groupId: data.group_id,
                      usedFilterLabels: prev
                        .filter((f) => f.filter_id !== filter_id)
                        .map((f) => f.label),
                      onFormFinish: handleFilterChange(filter_id),
                    });

                    return { ...filter, editData: data.rules };
                  })
                );

                resolve();
              },
              (reason: AxiosError) => {
                reject(reason);
                Log.error(`Failed to get data for filter ${label}!`, reason);
              }
            );
          });
        }

        return new Promise<void>((resolve) => {
          resolve();
          setFormProps({
            // modal opening
            open: true,
            filterId: filter_id,
            isCreate: isNew,
            label,
            data: editData,
            groupId: group_id,
            usedFilterLabels: filters.filter((f) => f.filter_id !== filter_id).map((f) => f.label),
            onFormFinish: handleFilterChange(filter_id),
          });
        });
      };

  const handleDeleteFilter = (filter: FilterData) => () => {
    const filterIndex = filters.map((f) => f.filter_id).indexOf(filter.filter_id);
    const newActiveTab =
      filters[filterIndex + 1]?.filter_id || filters[filterIndex - 1]?.filter_id || undefined;
    const deleteFilter = () => {
      setActiveTab(newActiveTab);
      setFilters((prev) => [...prev.filter((f) => f.filter_id !== filter.filter_id)]);
    };

    if (filter.isNew) {
      deleteFilter();
      return Promise.resolve();
    }

    return new Promise<void>(async (resolve, reject) => {
      try {
        await axios.delete<BasicResponseDTO>(`/api/configuration/v1/tracker/filter/${filter.filter_id}`);
        deleteFilter();
        Log.success(`Successfully deleted filter ${filter.label}!`);
        resolve();
      } catch (error) {
        Log.error(`Failed to delete filter ${filter.label}!`, error as AxiosError);
        reject(error instanceof Error ? error : new Error(`Failed to delete filter ${filter.label}`));
      }
    });
  };

  const handleCloseModal = () => setFormProps((prev) => ({ ...prev, open: false }));
  const handleTabChange = (tabKey: string) => {
    if (activeTab !== tabKey)
      // click on delete ok in deletion Modal cause this handle call after delete set
      setActiveTab(tabKey);
  };

  return (
    <>
      <XmTabsSticky
        className='xm-tab-filters'
        activeKey={activeTab}
        tabPlacement='start'
        onTabClick={handleTabChange}
        tabBarExtraContent={{
          left: (
            <XmButton className='xm-btn-add-filter' onClick={handleAddFilter}>
              New Filter
            </XmButton>
          ),
        }}
        items={filters.map((filter) => ({
          key: filter.filter_id,
          children: (
            <CfgTrackerPart
              showTechnologyColumn
              params={{ filter_id: filter.filter_id }}
              isNew={filter.isNew}
            />
          ),
          label: (
            <FilterTabLabel
              label={filter.label}
              isNew={filter.isNew}
              onEdit={handleEditFilter(filter)}
              onDelete={handleDeleteFilter(filter)}
            />
          ),
        }))}
      />
      {formProps && (
        <FilterModal {...formProps} onOk={handleCloseModal} onCancel={handleCloseModal} />
      )}
    </>
  );
};

function setNewFilterName(names: string[]): string {
  let newName: string;
  let count = 1;
  while (!newName) {
    const exists = names.some((name) => name == `New filter ${count}`);
    if (exists) count++;
    else newName = `New filter ${count}`;
  }

  return newName;
}

interface PropsFilterTabLabel {
  label: string;
  isNew: boolean;
  onDelete: () => Promise<void>;
  onEdit?: () => Promise<void>;
}

const FilterTabLabel = ({ label, isNew, onDelete, onEdit }: PropsFilterTabLabel) => {
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  const handleGenerics =
    (callPromise: () => Promise<void>, set: React.Dispatch<React.SetStateAction<boolean>>) =>
      () => {
        set(true);
        void callPromise().finally(() => set(false));
      };

  const { readonly } = GLOB.userInfo;

  return (
    <div className='xm-filter-label'>
      <span className={isNew ? 'xm-new-filter' : ''}>{label}</span>
      <Button
        onClick={handleGenerics(onEdit, setIsLoadingEdit)}
        type='link'
        size='small'
        className={isLoadingEdit ? 'xm-btn first' : 'xm-btn first hover-info'}
        disabled={isLoadingEdit}
        icon={<IconWithLoading isLoading={isLoadingEdit} icon={readonly ? <EyeOutlined /> : <EditOutlined />} />}
      />
      <Popconfirm
        title={`Delete ${label}`}
        icon={<QuestionCircleOutlined />}
        onConfirm={handleGenerics(onDelete, setIsLoadingDelete)}
        disabled={readonly}
      >
        <Button
          size='small'
          type='link'
          title='Delete filter'
          className={(readonly || isLoadingDelete) ? 'xm-btn' : 'xm-btn hover-danger'}
          disabled={readonly || isLoadingDelete}
          icon={<IconWithLoading isLoading={isLoadingDelete} icon={<DeleteOutlined />} />}
        />
      </Popconfirm>
    </div>
  );
};

const IconWithLoading = ({ isLoading, icon }: { isLoading: boolean; icon: React.ReactNode; }) => {
  if (!isLoading) return icon;

  return (
    <div className='xm-icon-container'>
      {icon}
      <XmSpinner size='small' />
    </div>
  );
};

export default CfgFilter;
